import { z } from "zod";
import {
  type SyncPracticeAppointmentTypeEffectiveAvailabilityResult,
  syncPracticeAppointmentTypeEffectiveAvailability,
} from "@/data/scheduling/sync-practice-appointment-type-effective-availability";
import { EFFECTIVE_AVAILABILITY_SYNC_REASON_VALUES } from "@/lib/calendar/effective-availability-sync-reasons";
import { createBackgroundJob } from "@/lib/inngest/utils";
import { createLogger } from "@/lib/logger";
import { createMedplumClient } from "@/lib/medplum/backend";

export const event = "vibrant/PracticeAppointmentTypeEffectiveAvailability.sync";
const logger = createLogger(
  "background/practice/on-practice-appointment-type-effective-availability-sync",
);

const InputSchema = z.object({
  practiceId: z.string().min(1),
  appointmentTypeId: z.string().min(1).optional(),
  providerId: z.string().min(1).optional(),
  roomId: z.string().min(1).optional(),
  actorProviderId: z.string().min(1).optional(),
  reason: z.enum(EFFECTIVE_AVAILABILITY_SYNC_REASON_VALUES).optional(),
});

type SyncPayload = z.infer<typeof InputSchema>;

const RETRY_DELAYS = ["10s", "30s"] as const;

export const onPracticeAppointmentTypeEffectiveAvailabilitySync = createBackgroundJob({
  event,
  id: "practice-appointment-type-effective-availability-sync",
  schema: InputSchema,
  // event.id: sync requests can legitimately repeat for the same practice/type/host combination.
  idempotency: "event.id",
  handler: async ({ payload, step, logger }) => {
    const maxAttempts = RETRY_DELAYS.length + 1;

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      try {
        const result = (await step.run(
          `sync-practice-effective-availability-attempt-${attempt}`,
          async (): Promise<SyncPracticeAppointmentTypeEffectiveAvailabilityResult> => {
            const medplum = createMedplumClient({ audience: "background" });
            return syncPracticeAppointmentTypeEffectiveAvailability(medplum, payload);
          },
        )) as SyncPracticeAppointmentTypeEffectiveAvailabilityResult;

        logger.info("Synced effective availability to child events", {
          ...payload,
          attempt,
          ...result,
        });

        return result;
      } catch (error) {
        const isFinalAttempt = attempt >= maxAttempts;
        const errorMessage = error instanceof Error ? error.message : String(error);
        if (isFinalAttempt) {
          logger.error("Effective availability sync failed after retries", {
            ...payload,
            attempt,
            error: errorMessage,
          });
          throw error instanceof Error ? error : new Error(errorMessage);
        }

        const retryDelay = RETRY_DELAYS[attempt - 1];
        logger.warn("Effective availability sync failed, retrying", {
          ...payload,
          attempt,
          retryDelay,
          error: errorMessage,
        });
        await step.sleep(`wait-before-effective-availability-sync-retry-${attempt}`, retryDelay);
      }
    }

    throw new Error("Failed to sync effective availability");
  },
});

export interface EffectiveAvailabilitySyncQueueOutcome {
  didQueue: boolean;
}

export async function triggerPracticeAppointmentTypeEffectiveAvailabilitySync(
  payload: SyncPayload,
): Promise<EffectiveAvailabilitySyncQueueOutcome> {
  await onPracticeAppointmentTypeEffectiveAvailabilitySync.trigger(payload);
  return { didQueue: true };
}
